import { Controller } from "@hotwired/stimulus"

// Replaces CONSLOG.Passwords.init() from the old app.
// Shows/hides password rules, validates requirements, shows inline errors on submit.
export default class extends Controller {
  static targets = ["password", "confirmation", "rules", "length", "uppercase", "lowercase", "submit", "requiredField"]
  static values = { confirm: { type: Boolean, default: false } }

  connect() {
    this.validClass = "text-success"
    this.invalidClass = "text-text-muted"
    this.errorBorder = "!border-red-500"
    this.errorText = "text-red-500"
    // Only manage disabled state for buttons that start disabled (reset/expired forms).
    // Registration form keeps its button always enabled and validates via submitForm().
    this.manageSubmitDisabled = this.hasSubmitTarget && this.submitTarget.disabled
  }

  validate() {
    const password = this.hasPasswordTarget ? this.passwordTarget.value : ""

    const lengthOk = password.length >= 8
    const uppercaseOk = /[A-Z]/.test(password)
    const lowercaseOk = /[a-z]/.test(password)
    const allPassed = lengthOk && uppercaseOk && lowercaseOk

    this._setRule("length", lengthOk)
    this._setRule("uppercase", uppercaseOk)
    this._setRule("lowercase", lowercaseOk)

    // Hide rules once all requirements are met
    if (this.hasRulesTarget && password.length > 0) {
      this.rulesTarget.classList.toggle("hidden", allPassed)
    }

    // Enable/disable submit button (only for forms that start with it disabled)
    if (this.manageSubmitDisabled) {
      let canSubmit = allPassed
      if (this.confirmValue && this.hasConfirmationTarget) {
        const conf = this.confirmationTarget.value
        canSubmit = canSubmit && conf.length > 0 && password === conf
      }
      this.submitTarget.disabled = !canSubmit
    }
  }

  clearError(event) {
    const field = event.currentTarget
    if (field.value.trim().length > 0) {
      field.classList.remove(this.errorBorder)
      const msg = field.parentElement.querySelector(".field-error")
      if (msg) msg.remove()
    }
  }

  submitForm(event) {
    let firstInvalid = null

    // Validate required fields
    this.requiredFieldTargets.forEach(field => {
      const empty = field.value.trim().length === 0
      this._toggleFieldError(field, empty, "This field is required")
      if (empty && !firstInvalid) firstInvalid = field
    })

    // Validate password requirements
    if (this.hasPasswordTarget) {
      const pw = this.passwordTarget.value
      const pwInvalid = pw.length < 8 || !/[A-Z]/.test(pw) || !/[a-z]/.test(pw)
      if (pw.length > 0 && pwInvalid) {
        this._toggleFieldError(this.passwordTarget, true, "Password does not meet requirements")
        if (this.hasRulesTarget) this.rulesTarget.classList.remove("hidden")
        if (!firstInvalid) firstInvalid = this.passwordTarget
      }

      // Validate confirmation match
      if (this.confirmValue && this.hasConfirmationTarget) {
        const conf = this.confirmationTarget.value
        const mismatch = conf.length > 0 && pw !== conf
        this._toggleFieldError(this.confirmationTarget, mismatch, "Passwords do not match")
        if (mismatch && !firstInvalid) firstInvalid = this.confirmationTarget
      }
    }

    if (firstInvalid) {
      event.preventDefault()
      firstInvalid.focus()
    }
  }

  _toggleFieldError(field, show, message) {
    const existing = field.parentElement.querySelector(".field-error")
    if (show) {
      field.classList.add(this.errorBorder)
      if (!existing) {
        const msg = document.createElement("p")
        msg.className = `field-error text-xs ${this.errorText} mt-1`
        msg.textContent = message
        field.insertAdjacentElement("afterend", msg)
      }
    } else {
      field.classList.remove(this.errorBorder)
      if (existing) existing.remove()
    }
  }

  _setRule(name, passed) {
    const target = `${name}Target`
    if (!this[`has${name.charAt(0).toUpperCase() + name.slice(1)}Target`]) return

    const el = this[target]
    if (passed) {
      el.classList.remove(this.invalidClass)
      el.classList.add(this.validClass)
    } else {
      el.classList.remove(this.validClass)
      el.classList.add(this.invalidClass)
    }
  }
}
